import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";

/**
 * Theme store — light / dark / "follow system".
 *
 * The resolved theme drives a single `.dark` class on <html>; tailwind.config
 * maps every surface and accent utility to CSS variables that flip under `.dark`,
 * so the whole UI re-themes with no per-component work.
 *
 * Persisted in localStorage; an inline anti-FOUC script in index.html applies the
 * same logic before Vue mounts so there's no white flash on a dark-mode reload.
 */
export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "cdp-theme";

export function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* localStorage disabled — non-fatal */ }
  return "system";
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

export const useThemeStore = defineStore("theme", () => {
  const mode = ref<ThemeMode>(readStoredMode());
  /** Tracks the OS preference; updated live when the user is in "system" mode. */
  const systemDark = ref(systemPrefersDark());

  /** The concrete theme actually shown: never "system". */
  const resolved = computed<"light" | "dark">(() =>
    mode.value === "system" ? (systemDark.value ? "dark" : "light") : mode.value,
  );

  const isDark = computed(() => resolved.value === "dark");

  function applyToDom(): void {
    try {
      const root = document.documentElement;
      root.classList.toggle("dark", resolved.value === "dark");
      // Hint native form controls / scrollbars to match.
      root.style.colorScheme = resolved.value;
    } catch { /* no DOM (tests/SSR) — non-fatal */ }
  }

  function setMode(m: ThemeMode): void {
    mode.value = m;
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
  }

  /** Header button toggles light ↔ dark only (based on what's currently shown).
   * "system" mode is no longer user-selectable (the monitor option was confusing),
   * but a previously-stored "system" is still honoured — the first toggle lands the
   * user on an explicit light/dark. */
  function cycle(): void {
    setMode(resolved.value === "dark" ? "light" : "dark");
  }

  /** Re-read the OS preference whenever it changes (matters only in "system" mode). */
  function bindSystemListener(): void {
    try {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent): void => { systemDark.value = e.matches; };
      if (mq.addEventListener) mq.addEventListener("change", handler);
      else if ((mq as MediaQueryList & { addListener?: (cb: (e: MediaQueryListEvent) => void) => void }).addListener) {
        // Safari < 14 fallback.
        (mq as MediaQueryList & { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener(handler);
      }
    } catch { /* ignore */ }
  }

  // Keep the DOM in sync with the resolved theme (applies immediately on init).
  watch(resolved, applyToDom, { immediate: true });

  return { mode, systemDark, resolved, isDark, setMode, cycle, applyToDom, bindSystemListener };
});
